import { useRouteLoaderData } from 'react-router'
import { type loader as rootLoader } from '#app/root.tsx'
import { type SerializeFrom } from '#app/utils/serialize'

function isUser(
	user: unknown,
): user is SerializeFrom<typeof rootLoader>['user'] {
	return (
		typeof user === 'object' &&
		user !== null &&
		'id' in user &&
		typeof user.id === 'string'
	)
}

export function useOptionalUser() {
	const data = useRouteLoaderData<typeof rootLoader>('root')
	if (!data || !isUser(data.user)) {
		return undefined
	}
	return data.user
}

export function useUser() {
	const maybeUser = useOptionalUser()
	if (!maybeUser) {
		throw new Error(
			'No user found in root loader, but user is required by useUser. If user is optional, try useOptionalUser instead.',
		)
	}
	return maybeUser
}

type Action = 'create' | 'read' | 'update' | 'delete'
type Entity = 'user' | 'note' | 'event' | 'ticket'
type Access = 'own' | 'any' | 'own,any' | 'any,own'
/**
 * Always say which access is needed. A bare `update:event` used to match
 * `update:event:own` as well, which every account's default role holds, so a
 * staff-only gate let everyone through. Use `:any` for staff, `:own` for the
 * owner's own things and `:own,any` when either will do.
 */
export type PermissionString = `${Action}:${Entity}:${Access}`

export function parsePermissionString(permissionString: PermissionString) {
	const [action, entity, access] = permissionString.split(':') as [
		Action,
		Entity,
		Access | undefined,
	]
	// a bare string that got past the type (a cast) must not mean "any level
	// will do": fail loudly rather than pass everyone
	if (!access) {
		throw new Error(
			`Permission "${permissionString}" needs an access: own, any or own,any`,
		)
	}
	return { action, entity, access: access.split(',') as Array<Access> }
}

export function userHasPermission(
	user: Pick<ReturnType<typeof useUser>, 'roles'> | null | undefined,
	permission: PermissionString,
) {
	if (!user) return false
	const { action, entity, access } = parsePermissionString(permission)
	return user.roles.some((role) =>
		role.permissions.some(
			(permission) =>
				permission.entity === entity &&
				permission.action === action &&
				(!access || access.includes(permission.access)),
		),
	)
}

export function userHasRole(
	user: Pick<ReturnType<typeof useUser>, 'roles'> | null,
	role: string,
) {
	if (!user) return false
	return user.roles.some((r) => r.name === role)
}
